import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { Reveal, Overline } from "@/components/motion";
import Placeholder from "@/components/Placeholder";

const PRINCIPLES = [
  {
    n: "01",
    title: "Clarity before decoration",
    body: "Every decision starts with what the audience needs to understand. Restraint is a strategy, not a limitation.",
  },
  {
    n: "02",
    title: "Systems over one-offs",
    body: "I build reusable toolkits — grid, type, colour, motion — so teams ship consistently great work without me in the room.",
  },
  {
    n: "03",
    title: "Business first, always",
    body: "Design that can't be tied to a pursuit, a launch or a metric is decoration. I design for the outcome the business needs.",
  },
];

const TOOLS = [
  "Adobe InDesign",
  "Adobe Illustrator",
  "Adobe After Effects",
  "Adobe Photoshop",
  "Adobe XD",
  "Figma",
  "AI: Claude",
  "Runway",
];

export default function About() {
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div className="pt-16" data-testid="about-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 pt-16 sm:pt-24 pb-20 sm:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5">
            <div className="relative max-w-md">
              <div className="absolute -top-4 -right-4 w-full h-full border border-accent/40" />
              {settings?.portrait_url ? (
                <img
                  src={fileUrl(settings.portrait_url)}
                  alt="Twinkle Rajan — Delhi / Gurugram"
                  className="relative aspect-[3/4] w-full object-cover border border-border"
                />
              ) : (
                <Placeholder label="Portrait — coming soon" className="relative aspect-[3/4] border border-border" />
              )}
              <p className="mt-4 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                Twinkle Rajan — Delhi / Gurugram
              </p>
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <Overline>About</Overline>
              <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08]">
                Design that earns a seat at the{" "}
                <span className="italic text-accent">decision table</span>
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-5 text-lg leading-relaxed text-foreground/75 max-w-2xl" data-testid="about-bio">
                <p>
                  I'm a visual and brand designer with 10+ years of experience across brand
                  identity, presentations, publications, motion and interactive HTML.
                </p>
                <p>
                  I'm currently Senior Designer at Uniqus Consultech, a tech-enabled global
                  consulting firm, where I lead visual design across proposals,
                  thought-leadership publications, social and web — and manage a team of
                  junior designers.
                </p>
                <p className="text-muted-foreground text-base">
                  I studied at NIFT (B.Des, 2015).
                </p>
              </div>
              <div className="mt-10 flex flex-wrap gap-4">
                {settings.resume_url ? (
                  <a
                    href={fileUrl(settings.resume_url)}
                    download
                    data-testid="resume-download-button"
                    className="inline-flex items-center gap-3 bg-accent text-accent-foreground px-8 py-4 text-sm font-medium tracking-wide rounded-full transition-colors duration-300 hover:bg-foreground hover:text-background"
                  >
                    <Download size={16} /> Download resume
                  </a>
                ) : (
                  <button
                    onClick={() => toast.info("Resume will be available for download soon.")}
                    data-testid="resume-download-button"
                    className="inline-flex items-center gap-3 border border-foreground/25 px-8 py-4 text-sm font-medium tracking-wide rounded-full transition-colors duration-300 hover:border-accent hover:text-accent"
                  >
                    <Download size={16} /> Resume — on request
                  </button>
                )}
                <Link
                  to="/contact"
                  data-testid="about-contact-button"
                  className="inline-flex items-center gap-3 border border-foreground/25 px-8 py-4 text-sm font-medium tracking-wide rounded-full transition-colors duration-300 hover:border-accent hover:text-accent"
                >
                  Get in touch <ArrowUpRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-muted/40 py-20 sm:py-28" data-testid="about-principles-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <Overline>How I work</Overline>
            <h2 className="mt-4 mb-14 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Three principles
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1}>
                <div className="border-t-2 border-accent pt-6" data-testid={`about-principle-${p.n}`}>
                  <span className="font-mono text-xs text-accent">{p.n}</span>
                  <h3 className="mt-3 font-serif text-2xl tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="about-tools-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <Overline>Toolkit</Overline>
            <h2 className="mt-4 mb-12 font-serif text-3xl sm:text-4xl tracking-tight">Tools of the trade</h2>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
            {TOOLS.map((t, i) => (
              <Reveal key={t} delay={i * 0.06}>
                <div className="bg-background p-8 h-full flex items-center" data-testid={`tool-item-${i}`}>
                  <span className="font-serif text-xl sm:text-2xl tracking-tight">{t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
